import { useState, useEffect, createContext } from 'react'
import moment from 'moment'

import WorkoutNameInputBox from './InputBoxes/WorkoutNameInputBox'
import WorkoutPhase from './WorkoutPhase'
import AddPhaseButton from './Buttons/Phase/AddPhaseButton'

import WorkoutBuilderActionButton from './Buttons/Workout/WorkoutBuilderActionButton'

export const WorkoutTemplateBuilderContext = createContext()

export default function WorkoutTemplateBuilder({ 
    mode = 'view', 
    workoutTemplateJSON = null, 
    previousWeekWorkoutTemplateJSON = null, 
    actionButtonText = null,
    action = null 
}) {
    
    const workoutBuilderJSONVersion = '4.0'

    const phaseInitialObject = {
        name: '',
        phaseKey: '',
        type: 'circuit',
        note: '',
        circuitRounds: 3,
        exercises: [],
    }

    const exerciseInitialObject = {
        id: 0,
        name: '',
        note: '',
        exerciseType: '',
        lateral: '',
        setUnit: '',
        equipment: '',
        continuousOverride: false,
        continuousOverrideWeight: 50,
        showDetails: false,
        uniformReps: true,
        sets: [],
        restSecondsAfterExercise: 0,
        generationVariables: {
            fetchType: 'hard-coded',
        },
        supportedWorkoutLengths: [60, 40, 30],
    }

    const setInitialObject = { reps: 10, seconds: 60 }

    const workoutInitialObject = {
        name: moment().local().format('MMMM D') + ' Workout',
        version: workoutBuilderJSONVersion,
        phases: [JSON.parse(JSON.stringify(phaseInitialObject))],
    }

    const [phasedWorkout, setPhasedWorkout] = useState(null)

    // Mutates the workout
    const mutateWorkout = (mutationTag, value = null) => {
        // Create a copy of the array to avoid mutating the original state
        const tempJSON = { ...phasedWorkout }

        // Find correct mutation based on tag
        switch (mutationTag) {
            // Update workout name
            case 'change-workout-name':
                tempJSON.name = value
                break

            // Log error message if mutation tag mismatch
            default:
                console.error(mutationTag + ' is not a valid workout mutation tag!! phasedWorkout not mutated')
                break
        }

        // console.log(JSON.stringify(tempJSON, null, 2));

        // Update useState. This will trigger UI updates
        setPhasedWorkout(tempJSON)
    }

    // Mutates a workout phase
    const mutatePhase = (mutationTag, phaseIndex = null, value = null) => {
        // Create a copy of the array to avoid mutating the original state
        const tempJSON = { ...phasedWorkout }

        // Get phase helper if phaseIndex is not null
        const phase = phaseIndex !== null ? tempJSON.phases[phaseIndex] : null

        // Find correct mutation based on tag
        switch (mutationTag) {
            // Moves a phase up in the sequence
            case 'position-up':
                // Do nothing if user is trying to move up first exercise
                if (phaseIndex === 0)
                    break

                    // Swap the phases at the specified phase indices
                ;[tempJSON.phases[phaseIndex], tempJSON.phases[phaseIndex - 1]] = [tempJSON.phases[phaseIndex - 1], tempJSON.phases[phaseIndex]]

                break

            // Moves a phase down in the sequence
            case 'position-down':
                // Do nothing if user is trying to move down last exercise
                if (phaseIndex === phasedWorkout.phases.length - 1) return // Swap the phases at the specified phase indices
                ;[tempJSON.phases[phaseIndex], tempJSON.phases[phaseIndex + 1]] = [tempJSON.phases[phaseIndex + 1], tempJSON.phases[phaseIndex]]

                break

            // Update phase name
            case 'change-phase-name':
                phase.name = value
                phase.phaseKey = value.toLowerCase().replace(/\s+/g, '-')
                break

            // Update phase type
            case 'change-phase-type':
                // Reset set count to match circuitRounds when switching from straight to circuit
                if (phase.type === 'straight' && value === 'circuit') {
                    // For each exercise
                    for (let i = 0; i < phase.exercises.length; i++) {
                        phase.exercises[i].sets = []

                        // Add setsInitialObjects for the number of circuit rounds
                        for (let j = 0; j < phase.circuitRounds; j++) {
                            phase.exercises[i].sets.push(JSON.parse(JSON.stringify(setInitialObject)))
                        }

                        // Update Uniform Reps boolean
                        UpdateUniformRepsForAnExercise(phase.exercises[i])
                    }
                }

                phase.type = value

                break

            // Expands all exercises in a phase
            case 'expand-all':
                // Loop though all exercises and set showDetails to true
                for (let i = 0; i < phase.exercises.length; i++) {
                    phase.exercises[i].showDetails = true
                }
                break

            // Collapses all exercises in a phase
            case 'collapse-all':
                // Loop though all exercises and set showDetails to false
                for (let i = 0; i < phase.exercises.length; i++) {
                    phase.exercises[i].showDetails = false
                }
                break

            // Duplicates a phase and places it at that phase index
            case 'duplicate-phase':
                tempJSON.phases.splice(phaseIndex, 0, JSON.parse(JSON.stringify(phase)))
                break

            // Delete a phase from the workout builder
            case 'delete-phase':
                // Remove phase at index
                tempJSON.phases = [...tempJSON.phases.slice(0, phaseIndex), ...tempJSON.phases.slice(phaseIndex + 1)]

                break

            // Update phase note
            case 'change-phase-note':
                phase.note = value
                break

            // Append a new phase to phases array. Use empty phase object
            case 'add-phase':
                // Initializes to straight phase type
                tempJSON.phases = [...tempJSON.phases, JSON.parse(JSON.stringify(phaseInitialObject))]

                break

            // Adds a rep to an individual set
            case 'add-circuit-round':
                phase.circuitRounds += 1 // Increment rep count at setIndex by 1

                // For each exercise
                for (let i = 0; i < phase.exercises.length; i++) {
                    // Duplicate the last set
                    phase.exercises[i].sets.push(JSON.parse(JSON.stringify(phase.exercises[i].sets[phase.exercises[i].sets.length - 1])))

                    // Update Uniform Reps boolean
                    UpdateUniformRepsForAnExercise(phase.exercises[i])

                    // Verify that circuit rounds and sets array length are equal
                    if (phase.circuitRounds !== phase.exercises[i].sets.length) console.error('Mismatch between circuit rounds and exercise sets length!!')
                }

                break

            // Subtracts a rep from an individual set
            case 'subtract-circuit-round':
                // Floor rep count to 1
                if (phase.circuitRounds > 1) {
                    phase.circuitRounds -= 1 // Decrement rep count at setIndex by 1

                    // For each exercise
                    for (let i = 0; i < phase.exercises.length; i++) {
                        // Remove the last set
                        phase.exercises[i].sets = phase.exercises[i].sets.slice(0, -1)

                        // Update Uniform Reps boolean
                        UpdateUniformRepsForAnExercise(phase.exercises[i])

                        // Verify that circuit rounds and sets array length are equal
                        if (phase.circuitRounds !== phase.exercises[i].sets.length) console.error('Mismatch between circuit rounds and exercise sets length!!')
                    }
                }

                break

            // Log error message if mutation tag mismatch
            default:
                console.error(mutationTag + ' is not a valid phase mutation tag!! phasedWorkout not mutated')
                break
        }

        // console.log(JSON.stringify(tempJSON, null, 2));

        // Update useState. This will trigger UI updates
        setPhasedWorkout(tempJSON)
    }

    // Mutates a phase exercise
    const mutateExercise = (mutationTag, phaseIndex = null, exerciseIndex = null, setIndex = null, value = null) => {
        // Create a copy of the array to avoid mutating the original state
        const tempJSON = { ...phasedWorkout }

        // Get phase helper if phaseIndex is not null
        const phase = phaseIndex !== null ? tempJSON.phases[phaseIndex] : null

        // Get exercuse helper if phase and exerciseIndex is not null
        const exercise = phase !== null && exerciseIndex !== null ? phase.exercises[exerciseIndex] : null

        // Get set helper if phase, exercise, and setIndex is not null
        const set = phase !== null && exercise !== null && setIndex !== null ? exercise.sets[setIndex] : null

        // Find correct mutation based on tag
        switch (mutationTag) {
            // Moves an exerise up in the sequence
            case 'position-up':
                // Do nothing if user is trying to move up first exercise
                if (exerciseIndex === 0) return // Swap the elements at the specified indices
                ;[phase.exercises[exerciseIndex], phase.exercises[exerciseIndex - 1]] = [phase.exercises[exerciseIndex - 1], phase.exercises[exerciseIndex]]

                break

            // Moves an exerise down in the sequence
            case 'position-down':
                // Do nothing if user is trying to move down last exercise
                if (exerciseIndex === phase.exercises.length - 1) return // Swap the elements at the specified indices
                ;[phase.exercises[exerciseIndex], phase.exercises[exerciseIndex + 1]] = [phase.exercises[exerciseIndex + 1], phase.exercises[exerciseIndex]]

                break

            // Toggle show details boolean for an exercise
            case 'toggle-details':
                exercise.showDetails = !exercise.showDetails // Toggle boolen
                break

            // Appends a new set to the end of the sets array
            case 'add-set':
                // Add new set. Use the unit value from the last set
                exercise.sets = [...exercise.sets, JSON.parse(JSON.stringify(exercise.sets[exercise.sets.length - 1]))]

                // Update Uniform Reps boolean
                UpdateUniformRepsForAnExercise(exercise)

                break

            // Removes a set from the end of the sets array
            case 'subtract-set':
                // Floor sets length to 1
                if (exercise.sets.length > 1) {
                    exercise.sets = exercise.sets.slice(0, -1) // Remove last set

                    // Update Uniform Reps boolean
                    UpdateUniformRepsForAnExercise(exercise)
                }

                break

            // Removes a specific set in the exercises array
            case 'remove-specific-set':
                // If we are remove the last set, remove the entire exercise
                if (exercise.sets.length === 1) {
                    mutateExercise('delete-exercise', phaseIndex, exerciseIndex)
                    break
                }

                // Floor sets length to 1
                if (exercise.sets.length > 1) {
                    // Remove specific set at setIndex
                    exercise.sets = [...exercise.sets.slice(0, setIndex), ...exercise.sets.slice(setIndex + 1)]

                    // Update Uniform Reps boolean
                    UpdateUniformRepsForAnExercise(exercise)
                }

                break

            // Add a rep to all sets for a given exercise
            case 'uniform-add-rep':
                // Increment all sets rep count by 1
                for (let i = 0; i < exercise.sets.length; i++) exercise.sets[i].reps += 1

                break

            // Subtract a rep to all sets for a given exercise
            case 'uniform-subtract-rep':
                // Floor reps count to 1
                if (exercise.sets[0].reps > 1) {
                    // Decrement all sets rep count by 1
                    for (let i = 0; i < exercise.sets.length; i++) exercise.sets[i].reps -= 1
                }

                break

            // Adds a rep to an individual set
            case 'individual-add-rep':
                set.reps += 1 // Increment rep count at setIndex by 1

                // Update Uniform Reps boolean
                UpdateUniformRepsForAnExercise(exercise)

                break

            // Subtracts a rep from an individual set
            case 'individual-subtract-rep':
                // Floor rep count to 1
                if (set.reps > 1) {
                    // Decrement rep count at setIndex by 1
                    set.reps -= 1

                    // Update Uniform Reps boolean
                    UpdateUniformRepsForAnExercise(exercise)
                }

                break

            // Add 5 seconds to all sets for a given exercise
            case 'uniform-add-seconds':
                // Increment all sets seconds count by 5
                for (let i = 0; i < exercise.sets.length; i++) exercise.sets[i].seconds += 5
                break

            // Subtract 5 seconds from all sets for a given exercise
            case 'uniform-subtract-seconds':
                // Floor seconds to 5
                if (exercise.sets[0].seconds > 5) {
                    // Decrement all seconds by 5
                    for (let i = 0; i < exercise.sets.length; i++) exercise.sets[i].seconds -= 5
                }
                break

            // Adds 5 seconds to an individual set
            case 'individual-add-seconds':
                set.seconds += 5 // Increment seconds at setIndex by 5

                // Update Uniform Reps boolean
                UpdateUniformRepsForAnExercise(exercise)

                break

            // Subtracts 5 seconds from an individual set
            case 'individual-subtract-seconds':
                // Floor seconds to 5
                if (set.seconds > 5) {
                    // Decrement seconds at setIndex by 5
                    set.seconds -= 5

                    // Update Uniform Reps boolean
                    UpdateUniformRepsForAnExercise(exercise)
                }
                break

            // Duplicates a set
            case 'duplicate-set':
                exercise.sets.splice(setIndex, 0, JSON.parse(JSON.stringify(set)))
                break

            // Swaps an exercise out for another
            case 'swap-exercise':
                // Update exercise data points
                exercise.id = value.id
                exercise.name = value.name
                exercise.exerciseType = value.exerciseType
                exercise.lateral = value.lateral
                exercise.setUnit = value.setUnit
                exercise.equipment = value.equipment

                // Update Uniform Reps boolean
                UpdateUniformRepsForAnExercise(exercise)

                break

            // Duplicates an exercise
            case 'duplicate-exercise':
                phase.exercises.splice(exerciseIndex, 0, JSON.parse(JSON.stringify(exercise)))
                break

            // Deletes an exercise
            case 'delete-exercise':
                phase.exercises = [...phase.exercises.slice(0, exerciseIndex), ...phase.exercises.slice(exerciseIndex + 1)]
                break

            // Adds an exercise to the current phase
            case 'add-exercise':
                // Create new exercise object
                var addedExercise = JSON.parse(JSON.stringify(exerciseInitialObject))

                // Fill in exercise data
                addedExercise.id = value.id
                addedExercise.name = value.name
                addedExercise.exerciseType = value.exerciseType
                addedExercise.lateral = value.lateral
                addedExercise.setUnit = value.setUnit
                addedExercise.equipment = value.equipment

                // Add correct number of initial set objects to sets array based on phase type

                var newSetCount = 0

                switch (phase.type) {
                    case 'straight':
                        newSetCount = 3
                        break
                    case 'circuit':
                        newSetCount = phase.circuitRounds
                        break
                    default:
                        console.error(phase.type + ' is not a valid phase type!!')
                        newSetCount = 3
                        break
                }

                for (let i = 0; i < newSetCount; i++) addedExercise.sets.push(JSON.parse(JSON.stringify(setInitialObject)))

                // Add exercise to the end of exercises array
                tempJSON.phases[phaseIndex].exercises = [...tempJSON.phases[phaseIndex].exercises, addedExercise]

                break

            // Update phase note
            case 'change-exercise-note':
                exercise.note = value
                break

            // Adds 5 seconds to rest length. This is only called from RestCard
            case 'add-rest-seconds':
                exercise.restSecondsAfterExercise += 5 // Increment seconds by 5
                break

            // Subtracts 5 seconds to rest length. This is only called from RestCard
            case 'subtract-rest-seconds':
                // Floor rep count to 1
                if (exercise.restSecondsAfterExercise > 5) exercise.restSecondsAfterExercise -= 5 // Decrement seconds by 5

                break

            // Toggles the continuous ovveride
            case 'toggle-continuous-override':
                exercise.continuousOverride = !exercise.continuousOverride
                break

            // Adds 5 seconds to rest length. This is only called from RestCard
            case 'add-continuous-override-weight':
                exercise.continuousOverrideWeight += 5 // Increment seconds by 5
                break

            // Subtracts 5 seconds to rest length. This is only called from RestCard
            case 'subtract-continuous-override-weight':
                // Floor rep count to 1
                if (exercise.continuousOverrideWeight > 5) exercise.continuousOverrideWeight -= 5 // Decrement seconds by 5

                break

            // Log error message if mutation tag mismatch
            default:
                console.error(mutationTag + ' is not a valid exercise mutation tag!! phasedWorkout not mutated')
                break
        }

        // console.log(JSON.stringify(tempJSON, null, 2));

        // Update useState. This will trigger UI updates
        setPhasedWorkout(tempJSON)
    }

    // Updates uniform reps boolean for an exercise. Exercise is passed by reference
    const UpdateUniformRepsForAnExercise = (exercise) => {
        if (exercise != null) {
            var areRepsUniform = true

            // Needs to be atleast 2 sets
            if (exercise.sets.length > 1) {
                // Loop through sets
                for (let i = 1; i < exercise.sets.length; i++) {
                    // For type reps
                    if (exercise.setUnit === 'reps') {
                        // If we find that current does not equal previous, reps are not uniform
                        // Break out of the loop
                        if (exercise.sets[i - 1].reps !== exercise.sets[i].reps) {
                            areRepsUniform = false
                            break
                        }
                    }

                    // For type seconds
                    if (exercise.setUnit === 'seconds') {
                        // If we find that current does not equal previous, reps are not uniform
                        // Break out of the loop
                        if (exercise.sets[i - 1].seconds !== exercise.sets[i].seconds) {
                            areRepsUniform = false
                            break
                        }
                    }
                }
            }

            // Update boolen
            exercise.uniformReps = areRepsUniform
        }
    }

    const workoutTemplateBuilderData = {
        mode: mode,
        workoutTemplateJSON: phasedWorkout,
        previousWeekWorkoutTemplateJSON: previousWeekWorkoutTemplateJSON,
        mutateWorkout: mutateWorkout,
        mutatePhase: mutatePhase,
        mutateExercise: mutateExercise,
        actionButtonText: actionButtonText,
        action: action,
    }

    // On mount set the phased workout to initial object
    useEffect(() => {
        if (workoutTemplateJSON === null) setPhasedWorkout({ ...workoutInitialObject })
        else setPhasedWorkout(workoutTemplateJSON)
    }, [])

    if (phasedWorkout !== null) {
        return (
            <WorkoutTemplateBuilderContext.Provider value={workoutTemplateBuilderData}>
                <div className="space-y-4">
                    {/* Workout Name */}
                    <WorkoutNameInputBox />

                    {/* Workout Phases */}
                    {phasedWorkout.phases.map((phase, phaseIndex) => (
                        <div key={phaseIndex}>
                            <WorkoutPhase phase={phase} phaseIndex={phaseIndex} />
                        </div>
                    ))}

                    {/* Add Phase Button */}
                    <AddPhaseButton />

                    {/* Action Button */}
                    <WorkoutBuilderActionButton />
                </div>
            </WorkoutTemplateBuilderContext.Provider>
        )
    }
}
